import { useRef } from 'react';

import type { IPersonsListParams, Persons } from '@feathr/blackbox';

/*
 * Wraps `Persons.list()` for the donation form engagement table, forcing a
 * `reset: true` refetch when a (form, flavor, timeframe) `key` is revisited
 * so shared Person models don't keep another flavor's stale `inner_hits`.
 * Callers must feed `.models` into `<Table>` via `items`, not also
 * give `<Table>` its own `collection` + `params`.
 */
export function useDonationFormPersonsList(
  persons: Persons,
  params: IPersonsListParams,
  url: string,
  key: string,
): ReturnType<Persons['list']> {
  const seenKeys = useRef<Set<string>>(new Set());
  const lastKey = useRef<string | null>(null);

  // Only evaluate "is this a revisit?" when the key actually transitions to a
  // new value. A component can re-render multiple times for the *same* key
  // (e.g. a state update from an effect) — those re-renders must not be
  // mistaken for the user navigating back to a previously-seen combination.
  let isRevisit = false;
  if (key !== lastKey.current) {
    isRevisit = seenKeys.current.has(key);
    seenKeys.current.add(key);
    lastKey.current = key;
  }

  return persons.list(params, isRevisit ? { url, reset: true } : { url });
}
